/**
 * Block card styling - classes and CSS custom properties
 */
import { getLinkImageUrl } from "@shared/blocks/link/helpers";
import { getLinkPreviewImage } from "@shared/blocks/helpers";
import { lighten, darken } from "@shared/core/color";
import { isEmptyTiptapDoc } from "@shared/types/tiptap-document";
import { shouldRenderShippingFromLink } from "./shipping-link";
import { detectLinkPlatform } from "./platform";
const POSTIT_BASE_COLORS = [
    { notch: "#E2755B" }, // orange
    { notch: "#5EAF83" }, // green
    { notch: "#7C78EA" }, // purple
    { notch: "#545E04" },
    // { notch: "#FFDC4E" }, // yellow
    { notch: "#E5A7D1" }, // pink
    { notch: "#BBBBBB", border: "#ccc" }, // gray — manual border (lighten() washes out)
    { notch: "#99C7E1" }, // blue
];
// Exported so other surfaces (e.g. collection tabs in the app nav) can
// reuse the same palette that tints short text blocks. Keeping a single
// source of truth means the "same family of colors" reads consistently
// everywhere in the app.
export const POSTIT_PALETTE = POSTIT_BASE_COLORS.map((entry) => ({
    notch: lighten(entry.notch, 0.2),
    border: entry.border ?? lighten(entry.notch, 0.4),
    bg: entry.bg ?? lighten(entry.notch, 0.8),
    text: entry.text ?? darken(entry.notch, 0.55),
}));
const COLLECTION_TITLE_TARGET_CONTRAST = 4.5;
const COLLECTION_META_TARGET_CONTRAST = 4.5;
const COLLECTION_META_LIGHTEN_AMOUNT = 0.96;
const COLLECTION_SURFACE_SEARCH_STEP = 0.01;
const COLLECTION_FOOTER_SURFACE_LIGHTEN = 0.04;
const COLLECTION_SURFACE_EXTRA_DARKEN = 0.2;
export function hashStringToPaletteIndex(id, modulo) {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }
    return ((hash % modulo) + modulo) % modulo;
}
export function getCollectionPaletteColor(collectionId) {
    const index = hashStringToPaletteIndex(collectionId, POSTIT_PALETTE.length);
    return POSTIT_PALETTE[index];
}
function hexToRgb(hex) {
    return [
        parseInt(hex.slice(1, 3), 16),
        parseInt(hex.slice(3, 5), 16),
        parseInt(hex.slice(5, 7), 16),
    ];
}
function sRgbToLinear(channel) {
    const normalized = channel / 255;
    if (normalized <= 0.03928)
        return normalized / 12.92;
    return ((normalized + 0.055) / 1.055) ** 2.4;
}
function relativeLuminance(hex) {
    const [r, g, b] = hexToRgb(hex);
    return 0.2126 * sRgbToLinear(r) + 0.7152 * sRgbToLinear(g) + 0.0722 * sRgbToLinear(b);
}
function contrastRatio(a, b) {
    const aLuminance = relativeLuminance(a);
    const bLuminance = relativeLuminance(b);
    const lighter = Math.max(aLuminance, bLuminance);
    const darker = Math.min(aLuminance, bLuminance);
    return (lighter + 0.05) / (darker + 0.05);
}
function findAccessibleSurfaceDarkenAmount(notch) {
    for (let amount = 0; amount <= 1; amount += COLLECTION_SURFACE_SEARCH_STEP) {
        const baseSurface = darken(notch, amount);
        const footerSurface = lighten(baseSurface, COLLECTION_FOOTER_SURFACE_LIGHTEN);
        const titleContrast = contrastRatio("#FFFFFF", footerSurface);
        const metaContrast = contrastRatio(lighten(footerSurface, COLLECTION_META_LIGHTEN_AMOUNT), footerSurface);
        if (titleContrast >= COLLECTION_TITLE_TARGET_CONTRAST &&
            metaContrast >= COLLECTION_META_TARGET_CONTRAST) {
            return amount;
        }
    }
    return 1;
}
export function getCollectionCardColors(collectionId) {
    const palette = getCollectionPaletteColor(collectionId);
    const surfaceDarkenAmount = findAccessibleSurfaceDarkenAmount(palette.notch);
    const baseSurface = darken(palette.notch, surfaceDarkenAmount);
    const footerSurface = lighten(baseSurface, COLLECTION_FOOTER_SURFACE_LIGHTEN);
    const shellSurface = darken(footerSurface, COLLECTION_SURFACE_EXTRA_DARKEN);
    return {
        shellSurface,
        shellSurfaceHover: lighten(shellSurface, 0.06),
        footerSurface,
        border: lighten(footerSurface, 0.14),
        borderHover: lighten(footerSurface, 0.22),
        footerDivider: lighten(footerSurface, 0.2),
        title: "#FFFFFF",
        meta: lighten(footerSurface, COLLECTION_META_LIGHTEN_AMOUNT),
    };
}
const POSTIT_INDEX_STORAGE_KEY = "explorer-postit-color-indices";
const POSTIT_NEXT_INDEX_STORAGE_KEY = "explorer-postit-next-index";
const LEGACY_CARD_STYLE_STORAGE_KEY = "explorer-card-styles";
const POSTIT_COLOR_COUNT = POSTIT_PALETTE.length;
const MAX_POSTIT_INDEX_ENTRIES = 3000;
let cachedPostitIndexMap;
let cachedPostitNextIndex;
const cleanedLegacyIds = new Set();
function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i);
        hash = hash & hash;
    }
    return Math.abs(hash);
}
function loadPostitIndexMap() {
    if (typeof window === "undefined")
        return {};
    if (cachedPostitIndexMap)
        return cachedPostitIndexMap;
    try {
        const raw = localStorage.getItem(POSTIT_INDEX_STORAGE_KEY);
        if (!raw) {
            cachedPostitIndexMap = {};
            return cachedPostitIndexMap;
        }
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            const rawMap = parsed;
            const normalized = {};
            for (const [key, value] of Object.entries(rawMap)) {
                if (typeof key === "string" &&
                    Number.isInteger(value) &&
                    value >= 0 &&
                    value < POSTIT_COLOR_COUNT) {
                    normalized[key] = value;
                }
            }
            cachedPostitIndexMap = normalized;
            return cachedPostitIndexMap;
        }
    }
    catch {
        // Fall through to empty cache on parse/storage errors.
    }
    cachedPostitIndexMap = {};
    return cachedPostitIndexMap;
}
function capPostitIndexMap(map) {
    const entries = Object.entries(map);
    if (entries.length <= MAX_POSTIT_INDEX_ENTRIES)
        return map;
    const keep = entries.slice(entries.length - MAX_POSTIT_INDEX_ENTRIES);
    return Object.fromEntries(keep);
}
function loadPostitNextIndex() {
    if (typeof window === "undefined")
        return 0;
    if (cachedPostitNextIndex !== undefined)
        return cachedPostitNextIndex;
    try {
        const raw = localStorage.getItem(POSTIT_NEXT_INDEX_STORAGE_KEY);
        const parsed = raw ? Number(raw) : Number.NaN;
        if (Number.isInteger(parsed) && parsed >= 0) {
            cachedPostitNextIndex = parsed % POSTIT_COLOR_COUNT;
            return cachedPostitNextIndex;
        }
    }
    catch {
        // Fall through to zero on parse/storage errors.
    }
    cachedPostitNextIndex = 0;
    return cachedPostitNextIndex;
}
export function persistPostitColor() {
    if (typeof window === "undefined" || cachedPostitIndexMap === undefined)
        return;
    try {
        localStorage.setItem(POSTIT_INDEX_STORAGE_KEY, JSON.stringify(cachedPostitIndexMap));
        if (cachedPostitNextIndex !== undefined) {
            localStorage.setItem(POSTIT_NEXT_INDEX_STORAGE_KEY, String(cachedPostitNextIndex));
        }
    }
    catch {
        // Ignore persistence failures; runtime assignment still works in-memory.
    }
}
function getOrAssignPostitColorIndex(id) {
    if (typeof window === "undefined")
        return hashString(id) % POSTIT_COLOR_COUNT;
    const map = loadPostitIndexMap();
    const existing = map[id];
    if (Number.isInteger(existing) && existing >= 0 && existing < POSTIT_COLOR_COUNT) {
        delete map[id];
        map[id] = existing;
        return existing;
    }
    const nextIndex = loadPostitNextIndex();
    map[id] = nextIndex;
    cachedPostitIndexMap = capPostitIndexMap(map);
    cachedPostitNextIndex = (nextIndex + 1) % POSTIT_COLOR_COUNT;
    return nextIndex;
}
export function removeLegacyStickyStyle(cardId) {
    if (typeof window === "undefined")
        return;
    if (cleanedLegacyIds.has(cardId))
        return;
    cleanedLegacyIds.add(cardId);
    try {
        const raw = localStorage.getItem(LEGACY_CARD_STYLE_STORAGE_KEY);
        if (!raw)
            return;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
            return;
        const map = parsed;
        if (!(cardId in map))
            return;
        delete map[cardId];
        localStorage.setItem(LEGACY_CARD_STYLE_STORAGE_KEY, JSON.stringify(map));
    }
    catch {
        // Ignore legacy cleanup failures.
    }
}
function getPostitColor(id) {
    return POSTIT_PALETTE[getOrAssignPostitColorIndex(id)];
}
function getRandomRotation(id) {
    const hash = hashString(id + "rotation");
    return ((hash % 70) - 35) / 10; // -3.5 to 3.5 degrees
}
// ---------------------------------------------------------------------------
// Card Modifiers
// ---------------------------------------------------------------------------
const SHORT_TEXT_THRESHOLD = 150;
// Upper cap for the `card--text-md` (16px / vertically centred)
// typography tier. Above this, even a structurally one-paragraph text
// block reverts to the default body styling (top-anchored, default
// font-size) — the centred mid-step treatment was designed for short
// single sentences, and applying it to a 130-char list-shaped chunk
// produced a top-edge-clinging wall of bold-feeling text that read
// worse than the unstyled default.
const MEDIUM_TEXT_THRESHOLD = 80;
function isShortText(block) {
    if (block.type !== "text")
        return false;
    const content = block.content;
    return (content.text?.length ?? 0) < SHORT_TEXT_THRESHOLD;
}
// True when the body sits below `MEDIUM_TEXT_THRESHOLD` AND is
// structurally one paragraph. Anything longer — even a single-
// paragraph 130-char body — falls through to the default top-anchored
// body styling.
function isMediumText(block) {
    if (block.type !== "text")
        return false;
    const content = block.content;
    const length = content.text?.length ?? 0;
    if (length >= MEDIUM_TEXT_THRESHOLD)
        return false;
    return isSingleParagraphText(block);
}
/**
 * True when a text block has exactly one paragraph of body content.
 *
 * "One paragraph" means structurally one block of prose — no blank-line
 * breaks (plain-text path), or one top-level paragraph node in the rich
 * doc (Tiptap path). Headings, lists, blockquotes, or anything else
 * structural counts as "not a single paragraph" because those shapes
 * deserve their own block rhythm rather than the centred one-liner
 * treatment.
 *
 *   Rich doc:    `{ doc: { content: [{ type: "paragraph", … }] } }`  → true
 *   Rich doc:    `{ doc: { content: [<p>, <h2>] } }`                → false
 *   Plain text:  `"hello world"`                                    → true
 *   Plain text:  `"line one\nline two"`                             → true  (line break only)
 *   Plain text:  `"para one\n\npara two"`                           → false (blank line break)
 */
function isSingleParagraphText(block) {
    if (block.type !== "text")
        return false;
    const content = block.content;
    if (content.doc && isEmptyTiptapDoc(content.doc) === false) {
        const nodes = content.doc.content ?? [];
        return nodes.length === 1 && nodes[0]?.type === "paragraph";
    }
    const text = content.text ?? "";
    if (text.length === 0)
        return false;
    return /\n{2,}/.test(text) === false;
}
function hasPreview(block) {
    if (block.type !== "link")
        return false;
    const content = block.content;
    if (detectLinkPlatform(content) === "wikipedia")
        return false;
    return !!getLinkImageUrl(content);
}
// Index of the neutral gray entry inside POSTIT_BASE_COLORS / POSTIT_PALETTE.
// Long text blocks reuse the post-it chrome (border, notch, bg tint) but are
// pinned to this single hue so they don't get a randomly-colored "sticky note"
// look — they still need to read as a quieter "paper" surface.
const LONGTEXT_POSTIT_INDEX = 5;
// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------
/**
 * Get CSS classes for a block based on type and modifiers
 */
export function getBlockClasses(block) {
    const classes = [`card--${block.type}`];
    if (isShortText(block))
        classes.push("card--short");
    if (hasPreview(block))
        classes.push("card--has-preview");
    // Text-card typography tier — short single-paragraph bodies get the
    // centred 16px medium tier. The upper bound (MEDIUM_TEXT_THRESHOLD
    // = 80) keeps longer single-paragraph bodies — numbered lists,
    // multi-sentence notes — on the default top-anchored body styling
    // instead of getting the centred treatment that was designed for
    // short single sentences.
    if (isMediumText(block)) {
        classes.push("card--text-md");
    }
    if (block.type === "link") {
        const linkContent = block.content;
        // TODO(shipping-staging-fallback): once shipping metadata is guaranteed,
        // switch this class assignment back to `linkContent.shipping` only.
        // TODO(shipping-refresh): Keep style gating aligned with renderer routing:
        // shipping visual class should eventually depend on hydrated shipping payload only.
        if (shouldRenderShippingFromLink(linkContent)) {
            classes.push("card--shipping");
        }
        const platform = detectLinkPlatform(linkContent);
        if (platform)
            classes.push(`platform-${platform}`);
    }
    return classes;
}
/**
 * Get CSS custom properties for a block
 */
export function getBlockStyleVars(block) {
    const vars = {};
    vars["--card-rotation"] = `${getRandomRotation(block.id)}deg`;
    if (isShortText(block)) {
        const color = getPostitColor(block.id);
        vars["--postit-bg"] = color.bg;
        vars["--postit-border"] = color.border;
        vars["--postit-notch"] = color.notch;
        vars["--postit-text"] = color.text;
    }
    else if (block.type === "text") {
        // Long text uses the same post-it chrome but pinned to gray so it reads
        // as a quieter, neutral "paper" instead of competing with the colored
        // short-text postits.
        const color = POSTIT_PALETTE[LONGTEXT_POSTIT_INDEX];
        vars["--postit-bg"] = color.bg;
        vars["--postit-border"] = color.border;
        vars["--postit-notch"] = color.notch;
        vars["--postit-text"] = color.text;
    }
    return vars;
}
